import { Inject, Injectable, InjectionToken, Optional, Provider } from '@angular/core';
import { Observable, map, catchError, throwError, of, combineLatest } from 'rxjs';
import { v4 as uuidv4 } from 'uuid';

import { DeviceAuthenticationStatusService } from '@shure/shared/angular/data-access/device-authentication';
import {
	DeviceFeatureUpdateInput,
	NodeChangeType,
	ApolloQueryErrorMapper,
	ApolloQueryError,
	CombiningSubscriptionManager,
	SubscriptionManagerService,
	waitForGQLCompleteQueryOrThrow
} from '@shure/shared/angular/data-access/system-api/core';
import {
	GateOffBehavior,
	LightBehavior,
	LightColor,
	LightingRingStyle,
	Response,
	ResponseState,
	DeviceTransmitterLightingBehavior
} from '@shure/shared/angular/data-access/system-api/models';
import { DeviceReadonlyStateProvider } from '@shure/shared/angular/utils/device-readonly';
import { ILogger } from '@shure/shared/angular/utils/logging';
import { noMoreSubscribers, tapOnce } from '@shure/shared/typescript/rxjs';

import { LightsDevice } from '../../api';
import {
	GetDeviceQueryGQL,
	LightSettingsChannelsSubscriptionGQL,
	ClearErrorIndicatorMutationGQL,
	LightsSettingsDeviceSubscriptionGQL,
	UpdateDeviceLightingBrightnessMutationGQL,
	UpdateDeviceMutationGQL
} from '../graphql/generated/lights.generated';
import {
	mapLightsDeviceFromSysApi,
	mapLightingColorToSysApi,
	mapTransmitterDeviceLightingBehaviorToSysApi
} from '../mappers';

const LIGHTS_SETTINGS_EXCLUDE_MICROPHONE_ANGLE_TOKEN = new InjectionToken<boolean>(
	'LIGHTS_SETTINGS_EXCLUDE_MICROPHONE_ANGLE_TOKEN'
);

export function provideLightsSettingsExcludeMicrophoneAngle(): Provider {
	return {
		provide: LIGHTS_SETTINGS_EXCLUDE_MICROPHONE_ANGLE_TOKEN,
		useValue: true
	};
}

@Injectable()
export class DeviceSysApiService {
	private readonly logger: ILogger;

	private readonly lightSettingsDeviceGqlSubscription;
	private readonly lightSettingsChannelGqlSubscription;
	private readonly excludeMicrophoneAngle: boolean;

	constructor(
		logger: ILogger,
		private readonly getDeviceQueryGql: GetDeviceQueryGQL,
		private readonly updateDeviceMutationGQL: UpdateDeviceMutationGQL,
		private readonly clearErrorIndicatorMutationGQL: ClearErrorIndicatorMutationGQL,
		lightsSettingsDeviceSubscription: LightsSettingsDeviceSubscriptionGQL,
		private readonly updateDeviceLightingBrightnessMutationGQL: UpdateDeviceLightingBrightnessMutationGQL,
		lightSettingsChannelsSubscription: LightSettingsChannelsSubscriptionGQL,
		subscriptionManagerService: SubscriptionManagerService,
		private readonly deviceAuthenticationStatusService: DeviceAuthenticationStatusService,
		private readonly readonlyStateProvider: DeviceReadonlyStateProvider,
		@Inject(LIGHTS_SETTINGS_EXCLUDE_MICROPHONE_ANGLE_TOKEN)
		@Optional()
		excludeMicrophoneAngleParam?: boolean
	) {
		this.logger = logger.createScopedLogger('SysApiLightsDeviceApiService');

		this.excludeMicrophoneAngle = excludeMicrophoneAngleParam ?? false;

		this.lightSettingsDeviceGqlSubscription = new CombiningSubscriptionManager(
			{
				name: 'DeviceSysApiService:Device',
				changeTypes: [
					NodeChangeType.DeviceName,
					NodeChangeType.DeviceLightingBehavior,
					NodeChangeType.DeviceLightingSegmentsBehavior,
					NodeChangeType.DeviceLightingBrightness,
					NodeChangeType.DeviceLightingMuteBehavior,
					NodeChangeType.DeviceLightingUnmuteBehavior,
					NodeChangeType.DeviceAudioMute,
					NodeChangeType.DeviceIdentify,
					NodeChangeType.DeviceEthernetLights,
					NodeChangeType.DeviceLightingRing,
					NodeChangeType.DeviceLightingAutomixGating,
					NodeChangeType.DeviceAuthentication,
					NodeChangeType.DeviceErrorIndicator,
					NodeChangeType.DeviceConferencingOperationMode,
					NodeChangeType.DeviceLinkButtonLock,
					NodeChangeType.DeviceConfigurationLock,
					NodeChangeType.DeviceOnCharger,
					NodeChangeType.DeviceTransmitterLightingBehavior,
					NodeChangeType.DeviceLightingAudioActivity
				],
				subscriptionGql: lightsSettingsDeviceSubscription,
				additionalVariables: { excludeMicrophoneAngle: this.excludeMicrophoneAngle }
			},
			logger,
			subscriptionManagerService
		);

		const channelNodeChangeTypes = [
			NodeChangeType.AudioChannelLobeConfiguration,
			NodeChangeType.AudioChannelMicPolarPattern,
			NodeChangeType.AudioChannelMicrophoneAngle,
			NodeChangeType.AudioChannelMute,
			NodeChangeType.AudioChannelIdentify,
			NodeChangeType.AudioChannelCarriedByRfChannels,
			NodeChangeType.AudioChannelLightingBehavior
		];
		if (!this.excludeMicrophoneAngle) {
			channelNodeChangeTypes.push(NodeChangeType.AudioChannelMicrophoneAngle);
		}

		this.lightSettingsChannelGqlSubscription = new CombiningSubscriptionManager(
			{
				name: 'DeviceSysApiService:Device',
				changeTypes: channelNodeChangeTypes,
				subscriptionGql: lightSettingsChannelsSubscription,
				additionalVariables: { excludeMicrophoneAngle: this.excludeMicrophoneAngle }
			},
			logger,
			subscriptionManagerService
		);
	}

	public getDevice$(deviceId: string): Observable<LightsDevice> {
		const queryId = uuidv4();
		this.logger.trace('getDevice$()', 'Getting lights device by id', { deviceId, queryId });
		return combineLatest([
			this.getDeviceQueryGql
				.watch({
					variables: { id: deviceId, excludeMicrophoneAngle: this.excludeMicrophoneAngle },
					fetchPolicy: 'network-only',
					errorPolicy: 'all'
				})
				.valueChanges.pipe(waitForGQLCompleteQueryOrThrow()),
			this.deviceAuthenticationStatusService.getDeviceState$(deviceId),
			this.readonlyStateProvider.isReadonly$(deviceId)
		]).pipe(
			map(([query, auth, isReadonly]) => {
				if (!query.data?.node || !('isDeviceNode' in query.data.node)) {
					throw ApolloQueryErrorMapper.getError(query);
				}

				const device = mapLightsDeviceFromSysApi(query.data.node, auth.authenticated, isReadonly);
				this.logger.trace('getDevice$()', 'Query updated', { device });
				return device;
			}),
			tapOnce((device) => {
				this.lightSettingsDeviceGqlSubscription.subscribe(queryId, device.id);
				this.lightSettingsChannelGqlSubscription.subscribeToOnly(
					queryId,
					device.features.channels.map((channel) => channel.id)
				);
			}),
			noMoreSubscribers(() => {
				this.logger.trace('getDeviceQueryGql.onSubscribers.noMoreSubscribers', '', { queryId });
				this.lightSettingsDeviceGqlSubscription.unsubscribeQuery(queryId);
				this.lightSettingsChannelGqlSubscription.unsubscribeQuery(queryId);
			}),
			catchError((error: Error) => {
				this.logger.error('getDevice$()', 'Failed to query device for lights', {
					deviceId,
					error
				});
				return throwError(() => new ApolloQueryError(error));
			})
		);
	}

	public setBrightness(deviceId: string, value: number): Observable<Response<void, string>> {
		return this.updateDeviceLightingBrightnessMutationGQL
			.mutate({
				variables: {
					input: {
						id: deviceId,
						level: value
					},
					injectKeyring: '//supplied-by-keyring-injector'
				},
				errorPolicy: 'all'
			})
			.pipe(
				map((result) => {
					if (
						result.data?.updateDeviceLightingBrightness &&
						'error' in result.data.updateDeviceLightingBrightness
					) {
						this.logger.error('setBrightness()', 'Failed to update brightness', {
							deviceId,
							result
						});

						return { state: ResponseState.Error, error: 'error' };
					}

					this.logger.trace('setBrightness()', 'Done', { deviceId });
					return { state: ResponseState.Done };
				}),
				catchError((error: Error) => {
					this.logger.error('setBrightness()', 'Error', { error });
					return of({ state: ResponseState.Error, error: error.message });
				})
			);
	}

	public clearLedErrors(deviceId: string): Observable<Response<void, string>> {
		this.logger.trace('clearLedErrors()', 'clearing device led errors', { deviceId });

		return this.clearErrorIndicatorMutationGQL
			.mutate({
				variables: { id: deviceId, injectKeyring: '//supplied-by-keyring-injector' },
				errorPolicy: 'all'
			})
			.pipe(
				map((result) => {
					if (result.data?.clearErrorIndicator.error) {
						this.logger.error('clearLedErrors', 'error', result.data.clearErrorIndicator.error);
						throw new Error(result.data.clearErrorIndicator.error.message);
					}

					this.logger.trace('clearLedErrors', 'Done', { deviceId });
					return { state: ResponseState.Done };
				}),
				catchError((error: Error) => {
					this.logger.error('clearLedErrors', 'Error', { error });
					return of({ state: ResponseState.Error, error: error.message });
				})
			);
	}

	public toggleNetworkJackLeds(deviceId: string, isOn: boolean): Observable<Response<void, string>> {
		this.logger.trace('toggleNetworkJackLeds()', 'toggling network jack LEDs', { deviceId, isOn });
		return this.updateDevice(deviceId, { ethernetLights: { enabled: isOn } });
	}

	public toggleAudioLightingActivity(deviceId: string, enabled: boolean): Observable<Response<void, string>> {
		this.logger.trace('toggleAudioLightingActivity()', 'toggling the audio level activity LEDs', {
			deviceId,
			enabled
		});
		return this.updateDevice(deviceId, { lightingAudioActivity: { enabled: enabled } });
	}

	public setLightRingStyle(deviceId: string, ringStyle: LightingRingStyle): Observable<Response<void, string>> {
		this.logger.trace('setLightRingStyle()', 'setting light ring style', { deviceId, ringStyle });
		return this.updateDevice(deviceId, { lightingRing: { style: ringStyle } });
	}

	public toggleAutomixGating(deviceId: string, enabled: boolean): Observable<Response<void, string>> {
		this.logger.trace('toggleAutomixGating()', 'toggling automix gating', { deviceId, enabled });
		return this.updateDevice(deviceId, { lightingAutomixGating: { enabled: enabled } });
	}

	public setGateOffBehavior(deviceId: string, behavior: GateOffBehavior): Observable<Response<void, string>> {
		this.logger.trace('setGateOffBehavior()', 'setting gate off behavior', { deviceId, behavior });
		return this.updateDevice(deviceId, { lightingAutomixGating: { automixerInputDisengagedBehavior: behavior } });
	}

	public setMuteColor(deviceId: string, color: LightColor): Observable<Response<void, string>> {
		this.logger.trace('setMuteColor()', 'setting device mute color', { deviceId, color });

		return this.updateDevice(deviceId, { lightingMuteBehavior: { color: mapLightingColorToSysApi(color) } });
	}

	public setUnmuteColor(deviceId: string, color: LightColor): Observable<Response<void, string>> {
		this.logger.trace('setUnmuteColor()', 'setting device unmute color', { deviceId, color });

		return this.updateDevice(deviceId, { lightingUnmuteBehavior: { color: mapLightingColorToSysApi(color) } });
	}

	public setUnmuteBehavior(deviceId: string, behavior: LightBehavior): Observable<Response<void, string>> {
		this.logger.trace('setUnmuteBehavior()', 'setting device unmute behavior', { deviceId, behavior });

		return this.updateDevice(deviceId, { lightingUnmuteBehavior: { mode: behavior } });
	}

	public setMuteBehavior(deviceId: string, behavior: LightBehavior): Observable<Response<void, string>> {
		this.logger.trace('setMuteBehavior()', 'setting device mute behavior', { deviceId, behavior });

		return this.updateDevice(deviceId, { lightingMuteBehavior: { mode: behavior } });
	}

	public setTransmitterLightingBehavior(
		deviceId: string,
		behavior: DeviceTransmitterLightingBehavior
	): Observable<Response<void, string>> {
		this.logger.trace('setTransmitterLightingBehavior()', 'setting transmitter device LEDs behavior', {
			deviceId,
			behavior
		});

		return this.updateDevice(deviceId, {
			transmitterLightingBehavior: { behavior: mapTransmitterDeviceLightingBehaviorToSysApi(behavior) }
		});
	}

	private updateDevice(deviceId: string, update: DeviceFeatureUpdateInput): Observable<Response<void, string>> {
		return this.updateDeviceMutationGQL
			.mutate({
				variables: {
					updates: [
						{
							device: {
								id: deviceId,
								features: update
							}
						}
					],
					injectKeyring: '//supplied-by-keyring-injector',
					excludeMicrophoneAngle: this.excludeMicrophoneAngle
				},
				errorPolicy: 'all'
			})
			.pipe(
				map((result) => {
					if (
						result.data?.updateNodes &&
						'error' in result.data.updateNodes[0] &&
						result.data.updateNodes[0].error !== null
					) {
						this.logger.error('updateDevice()', 'Failed to update device', {
							deviceId,
							result
						});
						return { state: ResponseState.Error, error: result.data.updateNodes[0].error?.message };
					}

					this.logger.trace('updateDevice()', 'Done', { deviceId });
					return { state: ResponseState.Done };
				}),
				catchError((error: Error) => {
					this.logger.error('updateDevice()', 'Error', { error });
					return of({ state: ResponseState.Error, error: error.message });
				})
			);
	}
}
